import { createContext, useContext } from 'react';
import { CalendarLayoutResolutionOutDTO } from '@api/ringil-api.v2';
import { CalendarLayoutConfig } from './model';
import { BUILT_IN_LAYOUT_CONFIG } from './defaults';

export interface CalendarLayoutContextValue {
  /** True while the bootstrap call is in flight; the built-in layout is served meanwhile. */
  loading: boolean;
  /** True once the bootstrap call has answered — successfully or not. */
  loaded: boolean;
  /**
   * The effective layout for a location: its own override, else the company-wide one, else the
   * system default, else the built-in layout. Never null, so callers never branch on "no config".
   */
  layoutFor: (locationId?: string) => CalendarLayoutConfig;
  /** Where `layoutFor` got its answer — for the designer's "this is what your calendar shows" hints. */
  resolutionFor: (locationId?: string) => CalendarLayoutResolutionOutDTO | null;
  /** The system default alone, ignoring overrides. */
  defaultConfig: CalendarLayoutConfig;
  /**
   * Fetches the layouts. Called by the calendar on every mount, so a layout published (by this user
   * in the designer, or by someone else) is picked up on the next visit rather than only after a
   * page reload; concurrent calls collapse into one request.
   *
   * Deliberately explicit rather than fetched by the provider itself: it wraps the whole app, but
   * only the calendar and the designer need layouts, and a carrier who never opens a calendar
   * should not pay for the call.
   */
  reload: () => void;
}

/**
 * Defaults to the built-in layout so a component rendered outside the provider (a preview, a test)
 * still draws the standard card instead of an empty one.
 */
export const CalendarLayoutContext = createContext<CalendarLayoutContextValue>({
  loading: false,
  loaded: false,
  layoutFor: () => BUILT_IN_LAYOUT_CONFIG,
  resolutionFor: () => null,
  defaultConfig: BUILT_IN_LAYOUT_CONFIG,
  reload: () => undefined,
});

/**
 * The only supported way to read the rendered layout. Components never call the API directly, so
 * the resolution rules live in exactly one place.
 *
 * Named `...Config` to keep it distinct from the calendar's existing `useCalendarLayout`, which is
 * about column paging.
 */
export const useCalendarLayoutConfig = (): CalendarLayoutContextValue => useContext(CalendarLayoutContext);
